import CustomSelect, {
  CustomSelectOption
} from 'components/shared/CustomSelect';
import { useI18n } from 'hooks/useI18n';
import { useState } from 'react';
import { getPublicPath } from 'utils/paths';
import SectionChart from '../shared/sectionChart/SectionChart';
import SectionHero from '../shared/sectionHero/SectionHero';
import Switcher from '../shared/switcher/Switcher';
import RolesDistribution from './charts/RolesDistribution';
import SoftSkillsCommunityVsOffered from './charts/SoftSkillsCommunityVsOffered';
import TalentByTechBackend from './charts/TalentByTechBackend';
import TalentByTechData from './charts/TalentByTechData';
import TalentByTechFrontend from './charts/TalentByTechFrontend';
import TalentByTechInfra from './charts/TalentByTechInfra';
import TalentByTechMobile from './charts/TalentByTechMobile';
import {
  EXPERIENCEBYROLETABS,
  LEASTWANTEDTECHSTABS,
  MOSTUSEDTECHSTABS,
  TALENTBYTECHOPTIONS
} from './Talent.constants';
import { StyledSection, StyledSelect } from './Talent.styled';

const Talent: React.FC = () => {
  const { t } = useI18n('developer-career-report-2024');
  const mostUsedTechTabs = MOSTUSEDTECHSTABS(t);
  const leastWantedTechTabs = LEASTWANTEDTECHSTABS(t);
  const experienceByRoleTabs = EXPERIENCEBYROLETABS(t);
  const talentByTechOptions = TALENTBYTECHOPTIONS(t);

  const [currentOption, setCurrentOption] = useState<string | number>('front');

  const handleOptionChange = ({ value }: CustomSelectOption): void => {
    setCurrentOption(value);
  };

  return (
    <StyledSection>
      <SectionHero
        image={getPublicPath('/images/stateSector/2024/talent.png')}
        title={t('mantechcar24.talent.hero.title')}
        desc={t('mantechcar24.talent.hero.desc')}
      />
      <SectionChart
        title={t('mantechcar24.talent.talent-per-tech.title')}
        shareId="talentpertech"
        emailSubject={t(
          'mantechcar24.share-modal.talent-per-tech.email-subject'
        )}
        emailMessage={t('mantechcar24.share-modal.talent-per-tech.email-text')}
        shareMessage={t('mantechcar24.share-modal.talent-per-tech.text')}
      >
        <StyledSelect>
          <CustomSelect
            options={talentByTechOptions}
            defaultValue={talentByTechOptions[0]}
            id="talentByTechSelect"
            onChange={handleOptionChange}
          />
        </StyledSelect>
        {currentOption === 'front' && <TalentByTechFrontend />}
        {currentOption === 'back' && <TalentByTechBackend />}
        {currentOption === 'mobile' && <TalentByTechMobile />}
        {currentOption === 'data' && <TalentByTechData />}
        {currentOption === 'infra' && <TalentByTechInfra />}
      </SectionChart>
      <SectionChart
        title={t('mantechcar24.talent.roles-distribution.title')}
        desc={t('mantechcar24.talent.roles-distribution.desc')}
        descExtra={t('mantechcar24.talent.roles-distribution.descExtra')}
        shareId="rolesdistribution"
        emailSubject={t(
          'mantechcar24.share-modal.roles-distribution.email-subject'
        )}
        emailMessage={t(
          'mantechcar24.share-modal.roles-distribution.email-text'
        )}
        shareMessage={t('mantechcar24.share-modal.roles-distribution.text')}
      >
        <RolesDistribution />
      </SectionChart>
      <SectionChart
        title={t('mantechcar24.talent.most-used-techs.title')}
        desc={t('mantechcar24.talent.most-used-techs.desc')}
        descExtra={t('mantechcar24.talent.most-used-techs.descExtra')}
        shareId="mostusedtechs"
        emailSubject={t(
          'mantechcar24.share-modal.most-used-techs.email-subject'
        )}
        emailMessage={t('mantechcar24.share-modal.most-used-techs.email-text')}
        shareMessage={t('mantechcar24.share-modal.most-used-techs.text')}
      >
        <Switcher
          tabsName={mostUsedTechTabs.tabsName}
          tabs={mostUsedTechTabs.tabs}
        />
      </SectionChart>
      <SectionChart
        title={t('mantechcar24.talent.least-wanted-techs.title')}
        desc={t('mantechcar24.talent.least-wanted-techs.desc')}
        descExtra={t('mantechcar24.talent.least-wanted-techs.descExtra')}
        shareId="leastwantedtechs"
        emailSubject={t(
          'mantechcar24.share-modal.least-wanted-techs.email-subject'
        )}
        emailMessage={t(
          'mantechcar24.share-modal.least-wanted-techs.email-text'
        )}
        shareMessage={t('mantechcar24.share-modal.least-wanted-techs.text')}
      >
        <Switcher
          tabsName={leastWantedTechTabs.tabsName}
          tabs={leastWantedTechTabs.tabs}
        />
      </SectionChart>
      <SectionChart
        title={t('mantechcar24.talent.main-techs.title')}
        desc={t('mantechcar24.talent.main-techs.desc')}
        descExtra={t('mantechcar24.talent.main-techs.descExtra')}
        shareId="experiencebyrole"
        emailSubject={t('mantechcar24.share-modal.main-techs.email-subject')}
        emailMessage={t('mantechcar24.share-modal.main-techs.email-text')}
        shareMessage={t('mantechcar24.share-modal.main-techs.text')}
      >
        <Switcher
          tabsName={experienceByRoleTabs.tabsName}
          tabs={experienceByRoleTabs.tabs}
        />
      </SectionChart>
      <SectionChart
        title={t('mantechcar24.talent.competences.title')}
        desc={t('mantechcar24.talent.competences.desc')}
        descExtra={t('mantechcar24.talent.competences.descExtra')}
        shareId="competences"
        emailSubject={t('mantechcar24.share-modal.competences.email-subject')}
        emailMessage={t('mantechcar24.share-modal.competences.email-text')}
        shareMessage={t('mantechcar24.share-modal.competences.text')}
      >
        <SoftSkillsCommunityVsOffered />
      </SectionChart>
    </StyledSection>
  );
};

export default Talent;
